import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api } from '../lib/api';
import { PlusIcon, MegaphoneIcon } from '@heroicons/react/24/outline';
import type { Campaign } from '../lib/types';

export default function Campaigns() {
  const { data, isLoading } = useQuery({
    queryKey: ['campaigns'],
    queryFn: () => api.get<{ data: Campaign[]; meta: { total: number; page: number; limit: number; total_pages: number } }>('/campaigns'),
  });

  const getStatusBadge = (status: Campaign['status']) => {
    const classes: Record<Campaign['status'], string> = {
      draft: 'badge-warning',
      scheduled: 'badge-info',
      sending: 'badge-success animate-pulse',
      completed: 'badge-info',
      paused: 'badge-warning',
    };
    return classes[status];
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold text-white">Campaigns</h1>
        <Link to="/campaigns/new" className="btn-primary">
          <PlusIcon className="w-5 h-5 mr-2" />
          New Campaign
        </Link>
      </div>

      {isLoading ? (
        <div className="card">
          <div className="animate-pulse space-y-4">
            {[...Array(5)].map((_, i) => (
              <div key={i} className="h-16 bg-dark-700 rounded-lg" />
            ))}
          </div>
        </div>
      ) : data?.data && data.data.length > 0 ? (
        <div className="card overflow-hidden p-0">
          <table className="w-full">
            <thead className="bg-dark-900">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Campaign</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Sent</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Opens</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">Clicks</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-dark-700">
              {data.data.map((campaign) => (
                <tr key={campaign.id} className="hover:bg-dark-700/50 transition-colors">
                  <td className="px-6 py-4">
                    <Link to={`/campaigns/${campaign.id}`} className="block">
                      <p className="font-medium text-white hover:text-primary-400">{campaign.name}</p>
                      <p className="text-sm text-gray-400 truncate max-w-xs">{campaign.subject}</p>
                    </Link>
                  </td>
                  <td className="px-6 py-4">
                    <span className={getStatusBadge(campaign.status)}>{campaign.status}</span>
                  </td>
                  <td className="px-6 py-4 text-white">{campaign.sent_count.toLocaleString()}</td>
                  <td className="px-6 py-4 text-white">{campaign.open_count.toLocaleString()}</td>
                  <td className="px-6 py-4 text-white">{campaign.click_count.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="card text-center py-16">
          <MegaphoneIcon className="w-16 h-16 mx-auto text-gray-600" />
          <h3 className="mt-4 text-xl font-medium text-white">No campaigns yet</h3>
          <p className="mt-2 text-gray-400">Create your first campaign to start sending emails</p>
          <Link to="/campaigns/new" className="btn-primary mt-6">
            <PlusIcon className="w-5 h-5 mr-2" />
            Create Campaign
          </Link>
        </div>
      )}
    </div>
  );
}
